'use client'

import { ReactNode, useState } from 'react'
import { Button, Cell, Checkbox, Dialog, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/shared/i18n'

import { FaqEntryType } from 'constants/faq-entry'
import FaqEntryUrl from 'components/FaqEntryUrl'

import { PersonalInformationSharingContentType } from '../../constants/personal-information-sharing'

import PersonalInformationSharingWarning from './PersonalInformationSharingWarning'

type Props = {
  contentType: PersonalInformationSharingContentType
  onEditClick: () => void
  onSendClick: () => void
  onLearnMoreClick: () => void
}

const PersonalInformationSharingModal = ({
  contentType,
  onEditClick,
  onSendClick,
  onLearnMoreClick,
}: Props) => {
  const [isAgreementChecked, setIsAgreementChecked] = useState(false)

  const translate = useTranslate('conversation.personal_information_sharing_dialog')

  const handleAgreementChange = () => {
    setIsAgreementChecked(prevState => !prevState)
  }

  const phishingExamplesLink = {
    'phishing-examples-link': (chunks: Array<ReactNode>) => (
      <FaqEntryUrl
        key="phishing-examples-link"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={onLearnMoreClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  return (
    <Dialog contentDimensions={{ maxWidth: '343px' }} testId="email-sharing-modal" show>
      <Cell>
        <Text
          as="h2"
          type={Text.Type.Heading}
          text={
            contentType === PersonalInformationSharingContentType.Email
              ? translate('email.title')
              : translate('phone.title')
          }
          alignment={Text.Alignment.Center}
        />
        <Spacer size={Spacer.Size.Large} />
        <PersonalInformationSharingWarning contentType={contentType} />
        <Spacer size={Spacer.Size.Large} />
        <Text as="p" text={translate('protect_your_data', phishingExamplesLink)} />
        <Spacer size={Spacer.Size.XLarge} />
        <Checkbox
          name="agreement-checkbox"
          text={
            contentType === PersonalInformationSharingContentType.Email
              ? translate('email.agreement_label')
              : translate('phone.agreement_label')
          }
          onChange={handleAgreementChange}
          checked={isAgreementChecked}
        />
        <Spacer size={Spacer.Size.XLarge} />
        <Button
          text={translate('actions.edit_message')}
          styling={Button.Styling.Filled}
          onClick={onEditClick}
          testId="email-sharing-modal--edit-button"
        />
        <Spacer size={Spacer.Size.Large} />
        <Button
          text={translate('actions.send')}
          styling={Button.Styling.Flat}
          onClick={onSendClick}
          disabled={!isAgreementChecked}
          testId="email-sharing-modal--send-button"
        />
      </Cell>
    </Dialog>
  )
}

export default PersonalInformationSharingModal
